/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import * as Yup from 'yup';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Local API & Types
import {
  QuestionPriority,
  useUpdateQuestionPriorityMutation,
} from '@app-types/blackroom-api';
import {
  DialogQuestionPriorityProps,
  DialogQuestionPriorityFormSchema,
} from './types';

// Style
import QuestionPrioritySelect from '@app/shared/ui-kit/molecules/QuestionPrioritySelect';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogQuestionPriority({
  questionIds,
  isSelectAll,
  scope,
}: DialogQuestionPriorityProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};

  // API
  const [updateQuestionPriority] = useUpdateQuestionPriorityMutation();

  // Locale
  const { t } = useTranslation(['Common', 'Support']);

  const refetchQuestions = useRefetchQuestions();

  // Form
  const formSchema = Yup.object().shape({
    priority: Yup.mixed<QuestionPriority>()
      .oneOf(Object.values(QuestionPriority))
      .required(),
  });
  const form = useForm<DialogQuestionPriorityFormSchema>({
    initialValues: {
      priority: QuestionPriority.Low,
    },
    validate: yupResolver(formSchema),
    onValuesChange: (formData) => {
      form.validate();
    },
  });

  /* ******************************** CALLBACKS ******************************* */

  async function onSubmit({ ...formArgs }: DialogQuestionPriorityFormSchema) {
    try {
      closeDialog?.('dialog-support-question-priority');
      await updateQuestionPriority({
        variables: {
          updateQuestionPriorityInput: {
            ...formArgs,
            questionIds,
            isSelectAll,
            scope,
            workspaceId: workspace.id,
          },
        },
      });
      refetchQuestions();
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Support:Common.errors.readonlyAccess'),
        color: 'blackroomBlack',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container
      w="20vw"
      onKeyDown={function cb(e) {
        switch (e.key) {
          case 'Enter':
            onSubmit(form.values);
            break;
          default:
            break;
        }
      }}
      fluid
    >
      <Stack align="stretch" h="100%">
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack>
            <QuestionPrioritySelect
              label={t(
                'Support:PageSupport.supportActions.dialogQuestionPriority.fields.priority.label',
              )}
              withAsterisk
              allowDeselect={false}
              selectedPriority={form.values.priority}
              {...form.getInputProps('priority')}
            />
          </Stack>
        </form>
        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.save'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={form.onSubmit(function cb(formValues) {
            onSubmit(formValues);
          })}
          onCancel={() => {
            dialogStore?.closeDialog?.('dialog-support-question-priority');
          }}
          disabled={!form.isValid()}
        />
      </Stack>
    </Container>
  );
}

export default DialogQuestionPriority;
